import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import AuthModal from "@/components/feature/AuthModal";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLang } from "@/contexts/LanguageContext";
import { mockPlayers } from "@/mocks/players";
import AnimBtn from "@/components/base/AnimBtn";

// ... existing code ...

type RankKey = "ownedBlocks" | "hourlyIncome" | "totalBuildings" | "coins" | "auctionWins";

const MEDAL: Record<number, { bg: string; text: string; icon: string }> = {
  0: { bg: "bg-yellow-400/20 border-yellow-400/40", text: "text-yellow-400", icon: "ri-trophy-fill" },
  1: { bg: "bg-gray-400/20 border-gray-400/40", text: "text-gray-300", icon: "ri-medal-fill" },
  2: { bg: "bg-orange-700/20 border-orange-700/40", text: "text-orange-400", icon: "ri-medal-fill" },
};

export default function RankingPage() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [activeRank, setActiveRank] = useState<RankKey>("ownedBlocks");

  const RANK_TABS: { id: RankKey; label: string; icon: string; color: string; unit: string }[] = [
    { id: "ownedBlocks",    label: t("rankOwnedBlocks"),    icon: "ri-map-pin-2-line",  color: "text-amber-400",  unit: t("unitBlocks") },
    { id: "hourlyIncome",   label: t("rankHourlyIncome"),   icon: "ri-coin-line",        color: "text-yellow-400", unit: t("unitCoinsH") },
    { id: "totalBuildings", label: t("rankTotalBuildings"), icon: "ri-building-2-line",  color: "text-emerald-400",unit: t("unitBuildings") },
    { id: "coins",          label: t("rankCoins"),          icon: "ri-safe-line",        color: "text-sky-400",    unit: t("unitCoins") },
    { id: "auctionWins",    label: t("rankAuctionWins"),    icon: "ri-auction-line",     color: "text-orange-400", unit: t("unitTimes") },
  ];

  const sorted = [...mockPlayers].sort((a, b) => b[activeRank] - a[activeRank]);
  const myRank = user ? sorted.findIndex((p) => p.id === user.id) : -1;
  const currentTab = RANK_TABS.find((t) => t.id === activeRank)!;

  const pageBg = isDark ? "bg-[#0a0a0f]" : "bg-[#f8f8f8]";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const rowHover = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";

  const top3 = sorted.slice(0, 3);
  const rest = sorted.slice(3);

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      <Navbar />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-5xl mx-auto">
        <div className="mb-8 text-center animate-slide-up-fade">
          <h1 className={`text-3xl font-black mb-2 ${textPrimary}`}>{t("rankingTitle")}</h1>
          <p className={`text-sm ${textSecondary}`}>{t("rankingDesc")}</p>
        </div>

        {/* Tabs */}
        <div className={`rounded-2xl border mb-8 overflow-hidden ${cardBg}`}>
          <div className="flex overflow-x-auto">
            {RANK_TABS.map((tab) => (
              <AnimBtn
                key={tab.id}
                onClick={() => setActiveRank(tab.id)}
                animType="pop"
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 border-b-2 ${
                  activeRank === tab.id ? `border-amber-400 ${tab.color}` : isDark ? "border-transparent text-gray-400 hover:text-white" : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
              </AnimBtn>
            ))}
          </div>
        </div>

        {/* Top 3 Podium */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[top3[1], top3[0], top3[2]].map((player, podiumIdx) => {
            if (!player) return <div key={podiumIdx} />;
            const rankIdx = podiumIdx === 0 ? 1 : podiumIdx === 1 ? 0 : 2;
            const medal = MEDAL[rankIdx];
            const heights = ["h-28", "h-36", "h-24"];
            const isMe = user?.id === player.id;
            return (
              <div key={player.id} className="flex flex-col items-center gap-3">
                <div className={`relative w-14 h-14 flex items-center justify-center rounded-2xl border-2 transition-all hover:scale-110 ${medal.bg} ${isMe ? "ring-2 ring-amber-400 animate-glow-pulse" : ""}`}>
                  <span className={`text-xl font-black ${medal.text}`}>{player.username.charAt(0).toUpperCase()}</span>
                  <div className={`absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full ${medal.bg} border ${medal.bg}`}>
                    <i className={`${medal.icon} text-xs ${medal.text}`}></i>
                  </div>
                </div>
                <div className="text-center">
                  <p className={`text-xs font-bold truncate max-w-[90px] ${isMe ? "text-amber-400" : textPrimary}`}>{player.username}</p>
                  <p className={`text-sm font-black ${currentTab.color}`}>
                    {player[activeRank].toLocaleString()}
                    <span className={`text-xs font-normal ml-1 ${textSecondary}`}>{currentTab.unit}</span>
                  </p>
                </div>
                <div className={`w-full rounded-t-xl ${heights[podiumIdx]} flex items-center justify-center border ${medal.bg}`}>
                  <span className={`text-2xl font-black ${medal.text}`}>{rankIdx + 1}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* My Rank */}
        {user && myRank >= 0 && (
          <div className={`rounded-2xl border p-4 mb-6 flex items-center gap-4 animate-success-burst ${isDark ? "bg-amber-400/5 border-amber-400/20" : "bg-amber-50 border-amber-200"}`}>
            <div className="w-10 h-10 flex items-center justify-center bg-amber-400/20 rounded-xl flex-shrink-0">
              <i className="ri-user-star-line text-amber-400 text-lg"></i>
            </div>
            <div className="flex-1">
              <p className={`text-xs ${textSecondary}`}>{t("yourRank")}</p>
              <p className={`font-bold ${textPrimary}`}>
                <span className="text-amber-400 text-xl font-black mr-1">#{myRank + 1}</span>
                {user.username}
              </p>
            </div>
            <div className="text-right">
              <p className={`text-xs ${textSecondary}`}>{currentTab.label}</p>
              <p className={`font-black text-lg ${currentTab.color}`}>
                {(sorted[myRank]?.[activeRank] ?? 0).toLocaleString()}
                <span className={`text-xs font-normal ml-1 ${textSecondary}`}>{currentTab.unit}</span>
              </p>
            </div>
          </div>
        )}

        {/* Table */}
        <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
          <div className={`grid grid-cols-12 gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-wider border-b ${isDark ? "text-gray-500 border-white/5" : "text-gray-400 border-gray-100"}`}>
            <div className="col-span-1 text-center">#</div>
            <div className="col-span-5">{t("colPlayer")}</div>
            <div className="col-span-2 text-right">{t("colBlocks")}</div>
            <div className="col-span-2 text-right">{t("colIncome")}</div>
            <div className="col-span-2 text-right">{currentTab.label}</div>
          </div>

          {sorted.slice(0, 3).map((player, idx) => {
            const medal = MEDAL[idx];
            const isMe = user?.id === player.id;
            return (
              <div key={player.id} className={`grid grid-cols-12 gap-2 px-5 py-4 items-center transition-all ${rowHover} hover:translate-x-1 border-b ${isDark ? "border-white/5" : "border-gray-100"} ${isMe ? isDark ? "bg-amber-400/5" : "bg-amber-50" : ""}`}>
                <div className="col-span-1 flex justify-center">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full border ${medal.bg}`}>
                    <i className={`${medal.icon} text-xs ${medal.text}`}></i>
                  </div>
                </div>
                <div className="col-span-5 flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm font-black flex-shrink-0 ${medal.bg}`}>
                    <span className={medal.text}>{player.username.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${isMe ? "text-amber-400" : textPrimary}`}>
                      {player.username}
                      {isMe && <span className="ml-1.5 text-xs bg-amber-400/20 text-amber-400 rounded-full px-1.5 py-0.5">{t("youBadge")}</span>}
                    </p>
                    <p className={`text-xs ${textSecondary}`}>{new Date(player.joinedAt).toLocaleDateString(lang === "ja" ? "ja-JP" : "en-US", { year: "numeric", month: "short" })} {t("joinedMonth")}</p>
                  </div>
                </div>
                <div className={`col-span-2 text-right text-sm font-semibold ${textPrimary}`}>{player.ownedBlocks}</div>
                <div className="col-span-2 text-right text-sm font-semibold text-emerald-400">+{player.hourlyIncome}</div>
                <div className={`col-span-2 text-right text-sm font-black ${currentTab.color}`}>{player[activeRank].toLocaleString()}</div>
              </div>
            );
          })}

          {rest.map((player, idx) => {
            const rank = idx + 4;
            const isMe = user?.id === player.id;
            return (
              <div key={player.id} className={`grid grid-cols-12 gap-2 px-5 py-3.5 items-center transition-all ${rowHover} hover:translate-x-1 ${idx !== rest.length - 1 ? isDark ? "border-b border-white/5" : "border-b border-gray-100" : ""} ${isMe ? isDark ? "bg-amber-400/5" : "bg-amber-50" : ""}`}>
                <div className="col-span-1 flex justify-center">
                  <span className={`text-sm font-bold ${textSecondary}`}>{rank}</span>
                </div>
                <div className="col-span-5 flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm font-black flex-shrink-0 ${isDark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                    {player.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${isMe ? "text-amber-400" : textPrimary}`}>
                      {player.username}
                      {isMe && <span className="ml-1.5 text-xs bg-amber-400/20 text-amber-400 rounded-full px-1.5 py-0.5">{t("youBadge")}</span>}
                    </p>
                    <p className={`text-xs ${textSecondary}`}>{new Date(player.joinedAt).toLocaleDateString(lang === "ja" ? "ja-JP" : "en-US", { year: "numeric", month: "short" })} {t("joinedMonth")}</p>
                  </div>
                </div>
                <div className={`col-span-2 text-right text-sm ${textPrimary}`}>{player.ownedBlocks}</div>
                <div className="col-span-2 text-right text-sm text-emerald-400">+{player.hourlyIncome}</div>
                <div className={`col-span-2 text-right text-sm font-bold ${currentTab.color}`}>{player[activeRank].toLocaleString()}</div>
              </div>
            );
          })}
        </div>

        {!user && (
          <div className={`mt-8 rounded-2xl border p-6 flex flex-col sm:flex-row items-center justify-between gap-4 ${cardBg}`}>
            <div>
              <h3 className={`font-bold mb-1 ${textPrimary}`}>{t("rankingCTATitle")}</h3>
              <p className={`text-sm ${textSecondary}`}>{t("rankingCTADesc")}</p>
            </div>
            <AnimBtn
              onClick={() => navigate("/map")}
              animType="bounce"
              className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-full px-8 py-3 cursor-pointer whitespace-nowrap"
            >
              {t("startNow")}
            </AnimBtn>
          </div>
        )}
      </div>
      <AuthModal />
    </div>
  );
}
